/**
 * QuizParticipantView - Main Orchestrator Component
 * 
 * This is the main entry point for quiz participation. It orchestrates
 * all screens and manages the participant lifecycle through the quiz.
 * 
 * Flow:
 * 1. Loading → Resolving session
 * 2. Error → If session not found
 * 3. Registration → Participant enters name
 * 4. Waiting → Before quiz starts
 * 5. Active → During quiz
 * 6. Results → After quiz ends
 * 
 * Usage:
 * <QuizParticipantView sessionIdOrShortCode="ABC123" />
 * 
 * Or with React Router:
 * <Route path="/play/:code" element={<QuizParticipantView />} />
 * 
 * Uses unifiedDataService for all RTDB operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Logger } from '../../utils/logger';

const log = Logger.create('QuizParticipantView');
import { RealtimeDBService, LiveSessionService } from '../../firebase/unifiedDataService';
import { useTheme } from '../../context/ThemeContext';
import { ThemeToggle } from '../../context/ThemeContext';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';

// Context
import { QuizParticipantProvider, useQuizParticipant } from './QuizParticipantContext';

// Screens
import { RegistrationScreen } from './screens/RegistrationScreen';
import { WaitingScreen } from './screens/WaitingScreen';
import { QuizActiveScreen } from './screens/QuizActiveScreen';
import { ResultsScreen } from './screens/ResultsScreen';

// Quiz states
const QUIZ_STATE = {
  LOADING: 'loading',
  ERROR: 'error',
  REGISTRATION: 'registration',
  WAITING: 'waiting',
  ACTIVE: 'active',
  RESULTS: 'results',
};

/**
 * Inner component that uses the context
 */
function QuizParticipantInner() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const {
    session,
    timer,
    answers,
    jokers,
    participant,
  } = useQuizParticipant();

  // Determine current state based on context
  const getCurrentState = () => {
    // Loading
    if (session.isLoading) {
      return QUIZ_STATE.LOADING;
    }

    // Error
    if (session.error) {
      return QUIZ_STATE.ERROR;
    }

    // Not registered yet
    if (!participant.isRegistered) {
      return QUIZ_STATE.REGISTRATION;
    }

    // Quiz explicitly ended by admin
    if (session.quizStatus === 'completed') {
      return QUIZ_STATE.RESULTS;
    }

    // Quiz hasn't started yet (index not set or explicitly negative)
    if (session.currentQuestionIndex === null || 
        session.currentQuestionIndex === undefined || 
        session.currentQuestionIndex < 0) {
      return QUIZ_STATE.WAITING;
    }

    // Quiz is active if we have valid question index
    if (session.currentQuestionIndex >= 0 && session.currentQuestionIndex < session.totalQuestions) {
      return QUIZ_STATE.ACTIVE;
    }

    // Quiz ended (all questions done or session closed)
    return QUIZ_STATE.RESULTS;
  };

  const currentState = getCurrentState();

  // Handle registration
  const handleRegistration = useCallback((id, name) => {
    participant.registerParticipant(id, name);
  }, [participant]);

  // Handle leave quiz - navigate based on user type
  const handleLeave = useCallback(() => {
    participant.leaveQuiz();
    // Navigate to appropriate page based on whether student is logged in
    if (participant.studentId) {
      // Logged-in student goes to dashboard
      navigate('/student/dashboard');
    } else {
      // Anonymous user goes to welcome page
      navigate('/');
    }
  }, [participant, navigate]);

  // Render based on current state
  switch (currentState) {
    case QUIZ_STATE.LOADING:
      return <LoadingScreen />;

    case QUIZ_STATE.ERROR:
      return <ErrorScreen error={session.error} navigate={navigate} />;

    case QUIZ_STATE.REGISTRATION:
      return (
        <RegistrationScreen
          sessionId={session.sessionId}
          shortCode={session.shortCode}
          settings={session.settings}
          participantCount={session.participants?.length || 0}
          instancePath={session.instancePath}
          onRegistered={handleRegistration}
        />
      );

    case QUIZ_STATE.WAITING:
      return (
        <WaitingScreen
          shortCode={session.shortCode}
          settings={session.settings}
          participants={session.participants || []}
          currentParticipantId={participant.participantId}
          currentParticipantName={participant.participantName}
          totalQuestions={session.questions?.length || 0}
          onLeave={participant.leaveQuiz}
        />
      );

    case QUIZ_STATE.ACTIVE:
      return (
        <QuizActiveScreen
          // Question props
          question={session.currentQuestion}
          questionIndex={session.currentQuestionIndex}
          totalQuestions={session.totalQuestions}
          selectedOptions={answers.selectedOptions}
          submitted={answers.submitted}
          showCorrectAnswer={session.showCorrectAnswer}
          answersLocked={session.answersLocked}
          
          // Timer props
          timeRemaining={timer.timeRemaining}
          timerExpired={timer.timerExpired}
          timerActive={timer.timerActive}
          urgencyLevel={timer.getUrgencyLevel()}
          timerColorClass={timer.getTimerTextColorClass()}
          timerBarColorClass={timer.getTimerColorClass()}
          bonusTime={timer.bonusTime}
          isPaused={timer.isPaused}
          hasTimer={timer.hasTimer}
          
          // Score props
          totalScore={answers.totalScore}
          currentStreak={answers.currentStreak}
          lastPointsEarned={answers.lastPointsEarned}
          pointsAwarded={answers.pointsAwarded}
          
          // Joker props
          availableJokers={jokers.availableJokers}
          jokerLoading={jokers.jokerLoading}
          hiddenOptions={jokers.hiddenOptions}
          audienceResults={jokers.audienceResults}
          bestPlayerAnswer={jokers.bestPlayerAnswer}
          
          // Handlers
          onSelectOption={answers.handleSelectOption}
          onSubmit={answers.submitAnswer}
          onUseFiftyFifty={jokers.useFiftyFiftyJoker}
          onUseAudience={jokers.useAudienceJoker}
          onUseBestPlayer={jokers.useBestPlayerJoker}
          onUseTimeExtension={jokers.useTimeExtensionJoker}
          
          // Settings
          enableGamification={session.settings?.enableGamification}
          enableJokers={session.settings?.enableJokers}
          multiAnswerMode={session.settings?.multiAnswerMode}
          shortCode={session.shortCode}
        />
      );

    case QUIZ_STATE.RESULTS:
      return (
        <ResultsScreen
          settings={session.settings}
          questions={session.questions}
          answersMap={answers.answersMap}
          totalScore={answers.totalScore}
          maxStreak={answers.maxStreak}
          participants={session.participants}
          currentParticipantId={participant.participantId}
          currentParticipantName={participant.participantName}
          onLeave={handleLeave}
        />
      );

    default:
      return <ErrorScreen error="Unbekannter Zustand" navigate={navigate} />;
  }
}

/**
 * Loading Screen Component
 */
function LoadingScreen() {
  const { darkMode } = useTheme();

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="text-center">
        {/* Spinner */}
        <div
          className={`
            inline-flex items-center justify-center w-16 h-16 rounded-full
            ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}
            animate-pulse mb-6
          `}
        >
          <svg
            className={`w-8 h-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} animate-spin`}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>

        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Quiz wird geladen...
        </h2>
        <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Bitte warten
        </p>
      </div>

      {/* Theme Toggle */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
    </div>
  );
}

/**
 * Error Screen Component
 */
function ErrorScreen({ error, navigate }) {
  const { darkMode } = useTheme();

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="text-center max-w-md">
        {/* Error Icon */}
        <div
          className={`
            inline-flex items-center justify-center w-16 h-16 rounded-full
            ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}
            mb-6
          `}
        >
          <svg
            className={`w-8 h-8 ${darkMode ? 'text-red-400' : 'text-red-600'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Quiz nicht gefunden
        </h2>
        <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {error || 'Der angegebene Quiz-Code ist ungültig oder die Session existiert nicht mehr.'}
        </p>

        {/* Retry Button */}
        <button
          onClick={() => navigate('/')}
          className={`
            mt-6 px-6 py-3 rounded-xl font-medium
            bg-indigo-600 text-white hover:bg-indigo-700
            theme-transition
          `}
        >
          Zur Startseite
        </button>
      </div>

      {/* Theme Toggle */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
    </div>
  );
}

/**
 * Main QuizParticipantView Component
 * 
 * Wraps the inner component with the context provider.
 */
export function QuizParticipantView({ sessionIdOrShortCode: propSessionId }) {
  // Get session ID from props, URL params, or query string
  const { code, sessionId: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUnifiedAuth();
  
  const sessionIdOrShortCode = propSessionId 
    || code 
    || urlSessionId 
    || searchParams.get('code') 
    || searchParams.get('session');

  // Handle error callback
  const handleError = useCallback((error) => {
    log.error('Quiz participant error', error);
  }, []);

  // If no session identifier provided, show error
  if (!sessionIdOrShortCode) {
    return <ErrorScreen error="Kein Quiz-Code angegeben" navigate={navigate} />;
  }

  return (
    <QuizParticipantProvider
      sessionIdOrShortCode={sessionIdOrShortCode}
      studentId={isAuthenticated && user ? user.uid : null}
      onError={handleError}
    >
      <QuizParticipantInner />
    </QuizParticipantProvider>
  );
}

// Also export as default
export default QuizParticipantView;

// Export quiz states for external use
export { QUIZ_STATE };
